// Section · Daily Intelligence Ledger + STRIKZA Verified Return Certificate.
//
// Doctrine source: STRIKZA_Daily_Intelligence_Ledger.docx + ACC v1.0 §10
// + STRIKZA_OddsJam_Gaps_v1.0.docx GAP-09 (timeline + trust-badge spec).
//
// What this section is for:
//   - The single most differentiating, trust-creating artifact STRIKZA
//     ships. It's the answer to "why should I believe you" in a market
//     full of tipsters publishing curated win rates.
//   - The doctrinal posture is: STRIKZA does NOT publish CLV, win
//     rates, or active-user counts before the ledger has 6+ months of
//     sealed data. Instead, every prediction is appended to a sealed
//     append-only ledger at 00:00 GMT each day. The ledger is the
//     trust mechanism. The Sika Verified Return Certificate is the
//     public artifact.
//   - World Cup 2026 is the launch moment per The_Ecosystem_Edge.docx.
//
// What this section is NOT:
//   - It is not a fake performance dashboard. It does not show fake
//     win rates or fabricated numbers. Every concrete number on the
//     page is an architecture fact, not a performance claim.

const LEDGER_PILLARS: ReadonlyArray<{
  step: string;
  title: string;
  body: string;
}> = [
  {
    step: "01",
    title: "Every prediction logged before kickoff",
    body: "Each agent run lands a row in the ledger with input hash, model id, token cost, latency, and structured output. No retroactive editing. No cherry-picking.",
  },
  {
    step: "02",
    title: "Sealed at 00:00 GMT daily",
    body: "Once midnight rolls over, the day's row is stamped immutable_at and append-only. Corrections (if a result is voided) ship as an additional row, never an overwrite.",
  },
  {
    step: "03",
    title: "Closing-line value tracked from day one",
    body: "STRIKZA records CLV vs Pinnacle / Asian closing on every signal — ledger row, signal id, settlement, the lot. The 90-day record is the gate.",
  },
  {
    step: "04",
    title: "STRIKZA Verified Return Certificate",
    body: "Within 4 months of launch, STRIKZA publishes a verified 90-day CLV+ record across at least 500 signals — third-party-confirmed, time-stamped, tamper-evident. Mathematically auditable. No pick is ever quietly removed.",
  },
];

const LEDGER_BANS: ReadonlyArray<string> = [
  "No published win rate before the Verified Return Certificate ships",
  "No fabricated active-chain counts",
  "No testimonials from un-attributable users",
  "No retroactive edits to settled signals",
  "No 'guaranteed', 'lock', 'sure win', 'banker' anywhere",
  "STRIKZA never executes a bet — every stake requires your tap",
];

export function LedgerSection(): JSX.Element {
  return (
    <section
      id="ledger"
      className="sz-l-section"
      style={{
        position: "relative",
        padding: "140px 0 160px",
        borderTop: "1px solid var(--border)",
        background: "linear-gradient(180deg, #0A0A0A 0%, #0C0B08 100%)",
      }}
    >
      <div className="container" style={{ position: "relative" }}>
        <div
          className="sz-l-section-header"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: 80,
            marginBottom: 72,
          }}
        >
          <div>
            <span className="bracket">Section · The ledger</span>
            <div
              className="mono"
              style={{
                fontSize: 11,
                letterSpacing: "0.18em",
                color: "var(--accent)",
                textTransform: "uppercase",
                marginTop: 18,
              }}
            >
              Verified within 4 months of launch
            </div>
          </div>
          <div>
            <h2
              className="serif"
              style={{
                fontSize: "clamp(40px, 5vw, 68px)",
                fontWeight: 500,
                lineHeight: 1.02,
                color: "var(--text)",
                marginBottom: 22,
                letterSpacing: "-0.01em",
              }}
            >
              We don&apos;t publish a win rate.{" "}
              <em style={{ color: "var(--accent)", fontStyle: "italic" }}>
                We publish the ledger.
              </em>
            </h2>
            <p
              style={{
                fontSize: 17,
                color: "var(--text-muted)",
                maxWidth: 640,
                lineHeight: 1.55,
              }}
            >
              Tipsters cherry-pick wins. STRIKZA seals every prediction every day at midnight,
              tracks closing-line value from day one, and within four months of launch publishes the{" "}
              <span style={{ color: "var(--text)" }}>STRIKZA Verified Return Certificate</span> — a
              third-party-audited, tamper-evident 90-day CLV+ record across at least 500 signals.
              Mathematically auditable. You can verify us before you pay us.
            </p>
          </div>
        </div>

        {/* Pillars */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 16,
            marginBottom: 64,
          }}
          className="sz-l-ledger-grid"
        >
          {LEDGER_PILLARS.map((p) => (
            <div
              key={p.step}
              style={{
                position: "relative",
                background: "var(--surface)",
                border: "1px solid var(--border)",
                padding: "28px 24px 26px",
                minHeight: 260,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: 22,
                  height: 22,
                  borderTop: "1px solid var(--accent)",
                  borderLeft: "1px solid var(--accent)",
                }}
              />
              <span
                className="mono"
                style={{
                  fontSize: 10,
                  letterSpacing: "0.18em",
                  color: "var(--accent)",
                  marginBottom: 18,
                }}
              >
                LEDGER.{p.step}
              </span>
              <div
                className="serif"
                style={{
                  fontSize: 22,
                  color: "var(--text)",
                  fontStyle: "italic",
                  fontWeight: 500,
                  marginBottom: 14,
                  lineHeight: 1.25,
                }}
              >
                {p.title}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--text-muted)",
                  lineHeight: 1.55,
                  marginTop: "auto",
                }}
              >
                {p.body}
              </div>
            </div>
          ))}
        </div>

        {/* What we don't do */}
        <div
          style={{
            padding: "32px 36px",
            border: "1px solid var(--border)",
            background: "rgba(17, 17, 17, 0.6)",
          }}
        >
          <div
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: "0.18em",
              color: "var(--accent)",
              textTransform: "uppercase",
              marginBottom: 18,
            }}
          >
            What the ledger forbids
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
            }}
            className="sz-l-ledger-bans"
          >
            {LEDGER_BANS.map((line, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 12,
                  padding: "10px 0",
                  borderBottom: i < LEDGER_BANS.length - 2 ? "1px solid var(--border)" : undefined,
                }}
              >
                <span
                  className="mono"
                  style={{
                    fontSize: 12,
                    color: "var(--accent-dim)",
                    flexShrink: 0,
                  }}
                >
                  ✕
                </span>
                <span style={{ fontSize: 14, color: "var(--text)" }}>{line}</span>
              </div>
            ))}
          </div>
        </div>

        {/* B2B preview line */}
        <div
          style={{
            marginTop: 32,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <p
            className="serif"
            style={{
              fontSize: 18,
              color: "var(--text-muted)",
              fontStyle: "italic",
              maxWidth: 640,
              textAlign: "center",
              lineHeight: 1.45,
            }}
          >
            The same ledger feeds the{" "}
            <span style={{ color: "var(--accent)" }}>B2B Intelligence API</span> — STRIKZA
            probability data as infrastructure for partners and operators. Developer waitlist opens
            2027.
          </p>
        </div>
      </div>
    </section>
  );
}
